import { useEffect, useMemo, useState } from "react";

import Sidebar from "../Sidebar";
import { useEventLogger } from "../../hooks/useEventLogger";
import {
  listEvalRuns,
  getEvalPayload,
  getEvalWerCsvUrl,
} from "../../services/api";

import { ChartRenderer, MetricTile } from "./EvalCharts";
import WerSection from "./WerSection";
import AgreementSection from "./AgreementSection";
import NormalizationReplayCard from "./NormalizationReplayCard";
import "./eval-dashboard.css";

const SUPPORTED_SCHEMA = "1";

const SECTION_CAPTIONS = {
  text_outliers:
    "Outliers are transcripts that DBSCAN labelled as noise (cluster -1) after TF-IDF (char 2-4) + SVD reduction. High % means heterogeneous transcripts.",
};

const PAYLOAD_CACHE_MS = 60_000;
const payloadCache = new Map(); // runId -> { ts, data }

async function fetchPayloadCached(runId) {
  const cached = payloadCache.get(runId);
  if (cached && Date.now() - cached.ts < PAYLOAD_CACHE_MS) {
    return cached.data;
  }
  const data = await getEvalPayload(runId);
  payloadCache.set(runId, { ts: Date.now(), data });
  return data;
}

function SkeletonView() {
  return (
    <>
      <div className="eval-skeleton" style={{ marginBottom: 16 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="eval-skeleton-tile" />
        ))}
      </div>
      <div className="eval-skeleton">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="eval-skeleton-chart" />
        ))}
      </div>
    </>
  );
}

function DatasetSummary({ dataset }) {
  if (!dataset) return null;
  const byLabel = dataset.by_label || {};
  const byAgeTier = dataset.by_age_tier || {};
  return (
    <div className="eval-summary-strip">
      <div className="eval-metric-tile">
        <div className="eval-metric-title">Total rows</div>
        <div className="eval-metric-value">{dataset.row_count?.toLocaleString() ?? "—"}</div>
      </div>
      {Object.entries(byLabel).map(([label, count]) => (
        <div key={label} className="eval-metric-tile">
          <div className="eval-metric-title">{label}</div>
          <div className="eval-metric-value">{count?.toLocaleString()}</div>
        </div>
      ))}
      {Object.keys(byAgeTier).length > 0 && (
        <div className="eval-metric-tile">
          <div className="eval-metric-title">By age tier</div>
          <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
            {Object.entries(byAgeTier).map(([tier, count]) => (
              <div key={tier} style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "1rem", fontWeight: 700 }}>{count?.toLocaleString()}</span>
                <span style={{ fontSize: "0.7rem", color: "var(--muted)" }}>{tier}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionView({ section, runId }) {
  if (section.id === "wer") {
    return <WerSection section={section} />;
  }
  const metrics = section.metrics || [];
  const charts = (section.charts || []).filter((c) => c);
  const caption = SECTION_CAPTIONS[section.id] || section.description;
  return (
    <div>
      {caption && <p className="eval-section-caption">{caption}</p>}
      {metrics.length > 0 && (
        <div className="eval-metrics-grid">
          {metrics.map((m) => (
            <MetricTile key={m.id} metric={m} />
          ))}
        </div>
      )}
      {charts.length > 0 && (
        <div className="eval-charts-grid">
          {charts.map((chart) => (
            <ChartRenderer key={chart.id} chart={chart} runId={runId} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function EvalDashboard({ user, onPageChange }) {
  const { logNavigation } = useEventLogger(user);
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState("latest");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);

  const visibleSections = useMemo(() => {
    if (!payload?.sections) return [];
    return payload.sections.filter((s) => {
      const hasCharts = Array.isArray(s.charts) && s.charts.length > 0;
      const hasMetrics = Array.isArray(s.metrics) && s.metrics.length > 0;
      return hasCharts || hasMetrics;
    });
  }, [payload]);

  useEffect(() => {
    listEvalRuns()
      .then((data) => setRuns(data.runs || []))
      .catch((err) => console.error("Failed to list runs", err));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPayloadCached(runId)
      .then((data) => {
        if (cancelled) return;
        setPayload(data);
        const first = (data.sections || []).find(
          (s) => (s.charts?.length || 0) + (s.metrics?.length || 0) > 0
        );
        setActiveSection(first?.id || null);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) setError("missing");
        else setError(err?.response?.data?.detail || err.message || "Failed to fetch payload");
        setPayload(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  const handleDownloadCsv = async () => {
    // CSV is stored as wer_per_row_<filename_id>.csv — use the dropdown's
    // runId (filename-based), not payload.run_id (the job's internal label).
    let concreteRunId = runId;
    if (!concreteRunId || concreteRunId === "latest") {
      const latest = runs.find((r) => r.run_id === "latest");
      concreteRunId = latest?.resolved_run_id || null;
    }
    if (!concreteRunId) return;
    setCsvLoading(true);
    try {
      const { url } = await getEvalWerCsvUrl(concreteRunId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      alert(`Could not get CSV link: ${detail}`);
    } finally {
      setCsvLoading(false);
    }
  };

  const handleRefresh = () => {
    payloadCache.delete(runId);
    setRunId((r) => r); // trigger re-fetch
    setLoading(true);
    fetchPayloadCached(runId)
      .then((data) => {
        setPayload(data);
        setError(null);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404) setError("missing");
        else setError(err?.response?.data?.detail || err.message);
      })
      .finally(() => setLoading(false));
  };

  const subtitle = payload
    ? `${payload.dataset?.name || ""} · ${payload.dataset?.row_count?.toLocaleString() ?? "?"} rows · ${
        payload.generated_at || ""
      }`
    : "";

  const schemaMismatch = payload && payload.schema_version && payload.schema_version !== SUPPORTED_SCHEMA;

  return (
    <div className="workspace">
      <Sidebar
        user={user}
        currentPage="eval-results"
        onPageChange={onPageChange}
        title={user?.name || user?.email}
        subtitle="STT Validation"
        onNavigation={logNavigation}
      />

      <div className="annotator">
        <div className="annotator-header">
          <h1>STT Validation Dashboard</h1>
          <div className="eval-header-row">
            <select
              className="eval-run-picker"
              value={runId}
              onChange={(e) => setRunId(e.target.value)}
            >
              <option value="latest">Latest run</option>
              {runs
                .filter((r) => r.run_id !== "latest")
                .map((r) => (
                  <option key={r.run_id} value={r.run_id}>
                    {r.label || `${r.run_id}${r.last_modified ? ` · ${new Date(r.last_modified).toLocaleString()}` : ""}`}
                  </option>
                ))}
            </select>
            <button className="eval-btn secondary" onClick={handleRefresh} disabled={loading}>
              Re-check
            </button>
            <button
              className="eval-btn"
              onClick={handleDownloadCsv}
              disabled={csvLoading || (runId === "latest" && !runs.find((r) => r.run_id === "latest")?.resolved_run_id) || !runId}
            >
              {csvLoading ? "Loading…" : "Download per-row WER CSV"}
            </button>
          </div>
        </div>

        <div className="eval-dashboard">
          {subtitle && <div className="eval-subtitle">{subtitle}</div>}

          {schemaMismatch && (
            <div className="eval-banner">
              Unsupported payload schema (expected v{SUPPORTED_SCHEMA}, got v{payload.schema_version}) — rendering best-effort.
            </div>
          )}

          {loading && <SkeletonView />}

          {!loading && error === "missing" && (
            <div className="eval-empty">
              <p>No dashboard data yet. The Devtron job hasn't produced a run.</p>
              <button className="eval-btn" onClick={handleRefresh}>Re-check</button>
            </div>
          )}

          {!loading && error && error !== "missing" && (
            <div className="eval-banner error">Error: {String(error)}</div>
          )}

          <AgreementSection />

          <NormalizationReplayCard
            runId={(() => {
              // Recompute keys S3 by the FILENAME-based run id (e.g.
              // "20260525T131219Z"), not the payload's internal `run_id`
              // (e.g. "dataset4200-omli-sarvam-v1") which is a different
              // identifier that doesn't match any S3 key. When the user
              // picked "latest", resolve via the runs list's resolved id.
              if (runId && runId !== "latest") return runId;
              const latest = runs.find((r) => r.run_id === "latest");
              return latest?.resolved_run_id || null;
            })()}
          />

          {!loading && payload && !error && (
            <>
              <DatasetSummary dataset={payload.dataset} />

              {visibleSections.length > 0 ? (
                <>
                  <div className="eval-tabs">
                    {visibleSections.map((s) => (
                      <button
                        key={s.id}
                        className={`eval-tab ${activeSection === s.id ? "active" : ""}`}
                        onClick={() => setActiveSection(s.id)}
                      >
                        {s.title}
                      </button>
                    ))}
                  </div>
                  {visibleSections
                    .filter((s) => s.id === activeSection)
                    .map((s) => (
                      <SectionView key={s.id} section={s} runId={payload?.run_id} />
                    ))}
                </>
              ) : (
                <div className="eval-empty">Payload has no charts or metrics yet.</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}